import logger from '../utils/logger';
// Dashboard cache utility with TTL and stale-while-revalidate pattern

const CACHE_PREFIX = 'dashboard_cache_';
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
const STALE_TTL = 15 * 60 * 1000; // 15 minutes - data is stale but usable

class DashboardCache {
  constructor() {
    this.memoryCache = new Map();
    this.pendingRequests = new Map();
  }

  // Get cache key
  getCacheKey(key, userId) {
    return `${CACHE_PREFIX}${userId}_${key}`;
  }

  // Get data from cache (memory first, then localStorage)
  get(key, userId) {
    const cacheKey = this.getCacheKey(key, userId);
    
    // Check memory cache first
    if (this.memoryCache.has(cacheKey)) {
      const cached = this.memoryCache.get(cacheKey);
      if (cached && !this.isExpired(cached.timestamp, STALE_TTL)) {
        return {
          data: cached.data,
          isStale: this.isExpired(cached.timestamp, DEFAULT_TTL),
          timestamp: cached.timestamp
        };
      }
    }

    // Check localStorage
    try {
      const stored = localStorage.getItem(cacheKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (!this.isExpired(parsed.timestamp, STALE_TTL)) {
          // Store in memory cache for faster access
          this.memoryCache.set(cacheKey, parsed);
          return {
            data: parsed.data,
            isStale: this.isExpired(parsed.timestamp, DEFAULT_TTL),
            timestamp: parsed.timestamp
          };
        }
      }
    } catch (error) {
      logger.error('UTILS', 'Cache read error:', { error: error });
    }

    return null;
  }

  // Set data in cache
  set(key, userId, data) {
    const cacheKey = this.getCacheKey(key, userId);
    const cacheData = {
      data,
      timestamp: Date.now()
    };

    // Store in memory
    this.memoryCache.set(cacheKey, cacheData);

    // Store in localStorage
    try {
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      logger.error('UTILS', 'Cache write error:', { error: error });
      // If localStorage is full, clear old dashboard cache
      this.clearOldCache();
    }
  }

  // Check if cache is expired
  isExpired(timestamp, ttl) {
    return Date.now() - timestamp > ttl;
  }

  // Clear old cache entries
  clearOldCache() {
    try {
      const keys = Object.keys(localStorage);
      
      keys.forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            if (this.isExpired(data.timestamp, STALE_TTL)) {
              localStorage.removeItem(key);
            }
          } catch {
            // Remove corrupted data
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      logger.error('UTILS', 'Cache cleanup error:', { error: error });
    }
  }

  // Stale-while-revalidate pattern
  async getWithRevalidate(key, userId, fetcher) {
    const cached = this.get(key, userId);
    
    // If we have fresh data, return it immediately
    if (cached && !cached.isStale) {
      return cached.data;
    }

    // Check if we're already fetching this data
    const cacheKey = this.getCacheKey(key, userId);
    if (this.pendingRequests.has(cacheKey)) {
      // If stale data exists, return it while waiting
      if (cached) {
        return cached.data;
      }
      // Otherwise wait for the pending request
      return this.pendingRequests.get(cacheKey);
    }

    // If we have stale data, return it immediately and fetch fresh in background
    if (cached && cached.isStale) {
      // Start background refresh
      const fetchPromise = fetcher()
        .then(data => {
          this.set(key, userId, data);
          this.pendingRequests.delete(cacheKey);
          return data;
        })
        .catch(error => {
          this.pendingRequests.delete(cacheKey);
          // On error, keep using stale data
          logger.error('UTILS', `Failed to refresh ${key}:`, { error: error });
          return cached.data;
        });

      this.pendingRequests.set(cacheKey, fetchPromise);
      
      // Return stale data immediately
      return cached.data;
    }

    // No cache or expired - fetch fresh data
    const fetchPromise = fetcher()
      .then(data => {
        this.set(key, userId, data);
        this.pendingRequests.delete(cacheKey);
        return data;
      })
      .catch(error => {
        this.pendingRequests.delete(cacheKey);
        throw error;
      });

    this.pendingRequests.set(cacheKey, fetchPromise);
    return fetchPromise;
  }

  // Clear all cache for a user
  clearUserCache(userId) {
    // Clear memory cache
    this.memoryCache.forEach((value, key) => {
      if (key.includes(`_${userId}_`)) {
        this.memoryCache.delete(key);
      }
    });

    // Clear localStorage
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(`${CACHE_PREFIX}${userId}_`)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      logger.error('UTILS', 'Cache clear error:', { error: error });
    }
  }

  // Prefetch data in background
  prefetch(key, userId, fetcher) {
    const cached = this.get(key, userId);
    
    // Only prefetch if data is stale or missing
    if (!cached || cached.isStale) {
      fetcher()
        .then(data => this.set(key, userId, data))
        .catch(error => logger.error('UTILS', `Prefetch failed for ${key}:`, { error: error }));
    }
  }
}

// Export singleton instance
const dashboardCache = new DashboardCache();
export default dashboardCache;